import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useCreatePatient } from '@/hooks/usePatients';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

/**
 * New patient form. Mirrors v1's `Clinio/src/pages/clinic/NewPatient.tsx`
 * field set: name, email, phone, CPR, address. CPR is optional and goes
 * straight to the create-patient Edge Function which encrypts it before
 * it ever lands on disk.
 */
export function NewPatient() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const create = useCreatePatient();
  const canSetCpr = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'patients_cpr', 'create'),
  );

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cpr, setCpr] = useState('');
  const [street, setStreet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Danmark');
  const [notes, setNotes] = useState('');
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!activeClinicId) {
      setSubmitError(t('patients.errors.no_clinic'));
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError(t('patients.errors.name_required'));
      return;
    }

    if (canSetCpr && cpr.trim().length > 0) {
      const digits = cpr.replace(/\D/g, '');
      if (digits.length !== 10) {
        setSubmitError(t('patients.errors.cpr_format'));
        return;
      }
    }

    setSubmitting(true);
    try {
      const created = await create.mutateAsync({
        clinic_id: activeClinicId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        cpr: canSetCpr ? cpr.trim() || undefined : undefined,
        address: {
          street: street.trim() || undefined,
          city: city.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
        },
        notes: notes.trim() || undefined,
        marketing_consent_email: marketingEmail,
        marketing_consent_sms: marketingSms,
      });
      toast.success(
        t('patients.toast_created', { name: `${created.first_name} ${created.last_name}` }),
      );
      navigate(`/patients/${created.id}`);
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'conflict') {
          setSubmitError(t('patients.errors.conflict'));
        } else if (err.code === 'invalid-input') {
          setSubmitError(t('patients.errors.invalid_input'));
        } else if (err.code === 'forbidden') {
          setSubmitError(t('patients.errors.forbidden'));
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError(t('patients.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-3xl px-6">
          <button
            type="button"
            onClick={() => navigate('/patients')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('patients.back_to_list')}
          </button>

          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
            {t('patients.new_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('patients.new_subtitle')}
          </p>

          {submitError && (
            <div className="mt-4">
              <ErrorAlert message={submitError} />
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
          >
            <Section title={t('patients.section_basic')}>
              <Grid>
                <Input
                  label={t('patients.field_first_name')}
                  name="first_name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <Input
                  label={t('patients.field_last_name')}
                  name="last_name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <Input
                  label={t('patients.field_email')}
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input
                  label={t('patients.field_phone')}
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Grid>
            </Section>

            {canSetCpr && (
              <Section
                title={t('patients.section_cpr')}
                hint={
                  <span className="inline-flex items-center text-emerald-700 dark:text-emerald-300">
                    <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                    {t('patients.cpr_encryption_note')}
                  </span>
                }
              >
                <Input
                  label={t('patients.field_cpr')}
                  name="cpr"
                  value={cpr}
                  onChange={(e) => setCpr(e.target.value)}
                  placeholder="1234567890"
                  hint={t('patients.field_cpr_hint')}
                />
              </Section>
            )}

            <Section title={t('patients.section_address')}>
              <Grid>
                <div className="sm:col-span-2">
                  <Input
                    label={t('patients.field_street')}
                    name="street"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                  />
                </div>
                <Input
                  label={t('patients.field_zip')}
                  name="zip_code"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                />
                <Input
                  label={t('patients.field_city')}
                  name="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                <div className="sm:col-span-2">
                  <Input
                    label={t('patients.field_country')}
                    name="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </div>
              </Grid>
            </Section>

            <Section title={t('patients.section_notes')}>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder={t('patients.field_notes_placeholder')}
              />
            </Section>

            <Section title={t('patients.section_consent')}>
              <div className="space-y-3">
                <ConsentToggle
                  checked={marketingEmail}
                  onChange={setMarketingEmail}
                  label={t('patients.consent_marketing_email')}
                  description={t('patients.consent_marketing_email_desc')}
                />
                <ConsentToggle
                  checked={marketingSms}
                  onChange={setMarketingSms}
                  label={t('patients.consent_marketing_sms')}
                  description={t('patients.consent_marketing_sms_desc')}
                />
              </div>
            </Section>

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/patients')}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={submitting || !activeClinicId}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('patients.save_new')
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {hint && <span className="text-xs">{hint}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function ConsentToggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
      />
      <span>
        <span className="block text-sm font-medium text-gray-900 dark:text-white">{label}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">{description}</span>
      </span>
    </label>
  );
}
